import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  roomChannelNames,
  type RoomConnectionState,
} from "@/lib/realtime/roomChannels";

export interface RoomActivityItem {
  id: string;
  room_id: string;
  user_id: string;
  action: string;
  content_title: string;
  content_type: string | null;
  created_at: string;
  user_name?: string;
  _new?: boolean; // flag for slide-in animation
}

/**
 * Initial fetch + realtime postgres-changes subscription for `room_activity`.
 * New rows are prepended and tagged with `_new` so the UI can animate them in.
 */
export function useRoomActivityRealtime(roomId: string | undefined) {
  const [activities, setActivities] = useState<RoomActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectionState, setConnectionState] =
    useState<RoomConnectionState>("disconnected");

  // Initial fetch
  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("room_activity")
        .select("*")
        .eq("room_id", roomId)
        .order("created_at", { ascending: false })
        .limit(20);

      if (cancelled) return;
      if (error || !data) {
        setActivities([]);
        setLoading(false);
        return;
      }

      const userIds = [...new Set(data.map((a) => a.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, name, email")
        .in("id", userIds);

      const map = new Map(
        (profiles || []).map((p) => [
          p.id,
          p.name || p.email?.split("@")[0] || "User",
        ]),
      );

      setActivities(
        data.map((a) => ({ ...a, user_name: map.get(a.user_id) || "User" })),
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [roomId]);

  // Realtime subscription
  useEffect(() => {
    if (!roomId) return;

    setConnectionState("connecting");
    const channel = supabase
      .channel(roomChannelNames.activity(roomId))
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "room_activity",
          filter: `room_id=eq.${roomId}`,
        },
        async (payload) => {
          const row = payload.new as RoomActivityItem;
          // Fetch sender name (single profile, cheap)
          const { data: profile } = await supabase
            .from("profiles")
            .select("name, email")
            .eq("id", row.user_id)
            .maybeSingle();
          const user_name =
            profile?.name || profile?.email?.split("@")[0] || "User";

          setActivities((prev) => {
            if (prev.some((a) => a.id === row.id)) return prev;
            return [{ ...row, user_name, _new: true }, ...prev].slice(0, 20);
          });

          // Clear the _new flag after animation
          setTimeout(() => {
            setActivities((prev) =>
              prev.map((a) => (a.id === row.id ? { ...a, _new: false } : a)),
            );
          }, 600);
        },
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") setConnectionState("connected");
        else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT")
          setConnectionState("reconnecting");
        else if (status === "CLOSED") setConnectionState("disconnected");
      });

    return () => {
      supabase.removeChannel(channel);
      setConnectionState("disconnected");
    };
  }, [roomId]);

  return { activities, loading, connectionState };
}
